import { UserProfileDisplay } from "@shared/components/UserProfileDisplay";
import theme from "@shared/utils/themeColors";
import { formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";
import { Space } from "../types/space.types";

interface SpaceMetadataProps {
  space: Space;
  onFollow?: (spaceId: string) => void;
  className?: string;
  fullView?: boolean;
}

/**
 * Reusable component for displaying space metadata (author, date, comments, follow button)
 */
const SpaceMetadata: React.FC<SpaceMetadataProps> = ({
  space,
  onFollow,
  className = "",
  fullView = false,
}) => {
  // Format the date consistently
  const formattedDate = space.createdAt
    ? space.createdAt instanceof Date
      ? formatDistanceToNow(space.createdAt, { addSuffix: true })
      : formatDistanceToNow(new Date(space.createdAt.toDate()), {
          addSuffix: true,
        })
    : "recently";

  // Handle follow button click
  const handleFollow = () => {
    if (onFollow && space.id) {
      onFollow(space.id);
    }
  };

  // For full view (used in SpaceEntries)
  return (
    <div className={`flex flex-wrap text-sm ${className}`}>
      {/* Author */}
      {fullView ? (
        <div className="flex items-center after:block after:content-['·'] last:after:content-[''] after:text-sm after:text-gray-400 dark:after:text-gray-600 after:px-2">
          <UserProfileDisplay
            profile={space.author}
            label="Created by"
            showAvatar={true}
            avatarSize="sm"
            className="w-full"
          />
        </div>
      ) : (
        <div className="flex items-center after:block after:content-['·'] last:after:content-[''] after:text-sm after:text-gray-400 dark:after:text-gray-600 after:px-2">
          <Link
            className={`font-medium ${theme.text.brand} ${theme.text.brandHover}`}
            to={`/community/profile/${space.author?.id || ""}`}
          >
            {space.author?.name || "Anonymous"}
          </Link>
        </div>
      )}

      {/* Date */}
      <div className="flex items-center after:block after:content-['·'] last:after:content-[''] after:text-sm after:text-gray-400 dark:after:text-gray-600 after:px-2">
        <span className={`${theme.text.muted}`}>{formattedDate}</span>
      </div>

      {/* Comments */}
      <div className="flex items-center after:block after:content-['·'] last:after:content-[''] after:text-sm after:text-gray-400 dark:after:text-gray-600 after:px-2">
        <Link
          className="font-medium text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
          to={`/spaces/${space.id}`}
        >
          <div className="flex items-center">
            <span>{space.commentCount || 0} comments</span>
          </div>
        </Link>
      </div>

      {/* Follow button */}
      <div className="flex items-center">
        <button
          className={`font-medium ${
            space.isFollowing
              ? theme.text.brand
              : `${theme.text.muted} ${theme.text.mutedHover}`
          }`}
          onClick={handleFollow}
        >
          <div className="flex items-center">
            <span>{space.isFollowing ? "Following" : "Follow"}</span>
          </div>
        </button>
      </div>
    </div>
  );
};

export default SpaceMetadata;
